<script setup lang="ts">
  import { computed } from "vue";
  import { ChevronDown, ChevronRight } from "lucide-vue-next";
  import CollapseCard from "@/components/episto-ui/collapse-card/CollapseCard.vue";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { ProgressBar } from "@/components/episto-ui/progress-bar";
  import { Table, TableCell, TableHead, TableRow } from "@/components/ui/table";
  import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
  import type { QuotaRow, TargetRow } from "./types";

  const props = defineProps<{
    target: TargetRow;
    visibleCollapse: number | string | (number | string)[];
  }>();

  defineEmits<{ toggle: [id: string] }>();

  const barColor = POPULATION_AUTO_COLORS[0];

  const isOpen = computed(() =>
    Array.isArray(props.visibleCollapse)
      ? props.visibleCollapse.includes(props.target.id)
      : props.visibleCollapse === props.target.id,
  );

  const formatNumber = (value: number): string => value.toLocaleString("fr-FR");

  const pluralize = (count: number, word: string) => `${count} ${word}${count > 1 ? "s" : ""}`;

  const summary = computed(() => {
    const quotaCount = props.target.questions.filter((question) => question.quotas.length > 0).length;
    if (quotaCount === 0) return "No quotas";
    const cellCount = props.target.questions.reduce((acc, question) => acc + question.quotas.length, 0);
    const progress = props.target.nCurrent > 0 ? pluralize(cellCount, "cell") : "not started";
    return `${pluralize(quotaCount, "quota")} · ${progress}`;
  });

  const barVariant = (quota: QuotaRow): "default" | "success" | "muted" => {
    if (quota.status === "follow_up") return "muted";
    return quota.status === "full" ? "success" : "default";
  };

  const statusLabel = (quota: QuotaRow): string => {
    if (quota.status === "follow_up") return "Follow-up";
    if (quota.status === "full") return "Full";
    if (quota.status === "below_min") return `${formatNumber(quota.remaining)} to min`;
    return `${formatNumber(quota.remaining)} to target`;
  };

  const statusClass: Record<QuotaRow["status"], string> = {
    below_min: "text-status-warning-strong",
    to_target: "text-muted-foreground",
    full: "text-status-success-strong",
    follow_up: "text-foreground-subtle",
  };
</script>

<template>
  <CollapseCard
    :collapse-name="target.id"
    :visible-collapse="visibleCollapse"
    :span-top-left="1"
    :span-top-center="5"
    :span-top-right="6"
    :span-bottom-left="0"
    :span-bottom-center="12"
    :span-bottom-right="0"
    @toggle="$emit('toggle', target.id)">
    <template #top-left>
      <ChevronDown
        v-if="isOpen"
        class="h-4 w-4 text-muted-foreground" />
      <ChevronRight
        v-else
        class="h-4 w-4 text-muted-foreground" />
    </template>

    <template #top-center>
      <div class="flex min-w-0 items-baseline gap-2.5">
        <span
          class="truncate heading-title"
          :title="target.name">
          {{ target.name }}
        </span>
        <span class="shrink-0 text-13 text-foreground-subtle">{{ summary }}</span>
      </div>
    </template>

    <template #top-right>
      <div
        class="ml-auto flex w-full items-center gap-4"
        @click.stop>
        <ProgressBar
          :percentage="target.percentage"
          :variant="target.percentage >= 100 ? 'success' : 'default'"
          :color="target.percentage >= 100 ? undefined : barColor"
          :show-min-indicator="false"
          size="sm"
          class="h-1.5 flex-1" />
        <span class="w-12 text-right text-sm font-semibold tabular-nums text-primary">{{ target.percentage }}%</span>
        <span class="w-24 text-right text-sm tabular-nums text-muted-foreground">
          {{ formatNumber(target.nCurrent) }} / {{ formatNumber(target.nTarget) }}
        </span>
      </div>
    </template>

    <template #bottom-center>
      <div
        v-if="target.hasQuotas"
        class="-mx-6 -mb-4">
        <Table class="table-fixed">
          <colgroup>
            <col />
            <col class="w-[280px]" />
            <col class="w-16" />
            <col class="w-16" />
            <col class="w-20" />
            <col class="w-32" />
          </colgroup>
          <tbody
            v-for="group in target.questions"
            :key="group.name">
            <TableRow class="border-t bg-muted/40 hover:bg-muted/40">
              <TableHead class="h-9 truncate pl-14">
                <span class="text-13 font-semibold text-primary">
                  {{ group.name }}
                </span>
                <span
                  v-if="group.title"
                  class="ml-2 text-13 font-normal text-foreground-subtle"
                  :title="group.title">
                  {{ group.title }}
                </span>
              </TableHead>
              <TableHead class="heading-label h-9 text-foreground-subtle">Progress</TableHead>
              <TableHead class="heading-label h-9 text-foreground-subtle text-right">%</TableHead>
              <TableHead class="heading-label h-9 text-foreground-subtle text-right">Min</TableHead>
              <TableHead class="heading-label h-9 text-foreground-subtle text-right">Target</TableHead>
              <TableHead class="heading-label h-9 text-foreground-subtle pr-6 text-right">Status</TableHead>
            </TableRow>
            <TableRow
              v-for="quota in group.quotas"
              :key="quota.id"
              class="last:border-b-0">
              <TableCell
                class="truncate py-2.5 pl-14 text-sm text-foreground"
                :title="quota.name">
                {{ quota.name }}
              </TableCell>
              <TableCell class="py-2.5">
                <div class="flex items-center gap-4">
                  <ProgressBar
                    :percentage="quota.percentage"
                    :variant="barVariant(quota)"
                    :color="quota.status === 'below_min' || quota.status === 'to_target' ? barColor : undefined"
                    :min-tolerance="quota.minTolerance"
                    :min-value="quota.minValue"
                    :show-min-indicator="!quota.isFollowUp"
                    :show-min-tooltip="false"
                    class="h-1.5 flex-1" />
                  <span class="w-10 text-right text-sm font-semibold tabular-nums text-primary">
                    {{ formatNumber(quota.nCurrent) }}
                  </span>
                </div>
              </TableCell>
              <TableCell class="py-2.5 text-right text-sm tabular-nums text-foreground-secondary">
                {{ quota.percentage }}%
              </TableCell>
              <TableCell class="py-2.5 text-right text-sm tabular-nums text-foreground-subtle">
                {{ quota.minValue ? formatNumber(quota.minValue) : "—" }}
              </TableCell>
              <TableCell class="py-2.5 text-right text-sm tabular-nums text-foreground-secondary">
                {{ quota.nTarget > 0 ? formatNumber(quota.nTarget) : "—" }}
              </TableCell>
              <TableCell
                class="py-2.5 pr-6 text-right text-13 font-medium tabular-nums"
                :class="statusClass[quota.status]">
                {{ statusLabel(quota) }}
              </TableCell>
            </TableRow>
          </tbody>
        </Table>
      </div>
      <EmptyState
        v-else
        class="w-full py-2"
        title="No quotas for this target." />
    </template>
  </CollapseCard>
</template>
